import { memo, useCallback } from 'react';
import { Control, FieldPath, FieldValues, useController, useFormContext } from 'react-hook-form';
import GuestPicker from './GuestPicker';

type GuestPickerProps = React.ComponentProps<typeof GuestPicker>;

type GuestFieldValue = {
  adults: string;
  children: string;
};

type RHFGuestPickerProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  GuestPickerProps,
  'numberOfAdults' | 'numberOfChildren' | 'onGuestCountChange' | 'error'
> & {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  onAfterChange?: (type: 'adults' | 'children', newValue: GuestFieldValue) => void;
};

function parseCount(count: string | number | undefined, fallback: number): number {
  if (count === undefined || count === null) return fallback;
  if (typeof count === 'number') return count;
  const parsed = Number.parseInt(count, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

const RHFGuestPickerInner = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control: controlProp,
  onAfterChange,
  ...props
}: RHFGuestPickerProps<TFieldValues>) => {
  const formContext = useFormContext<TFieldValues>();
  const control = controlProp ?? formContext?.control;

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control });

  const guestValue = value as unknown as GuestFieldValue | undefined;
  const adults = parseCount(guestValue?.adults, 1);
  const children = parseCount(guestValue?.children, 0);

  const handleGuestCountChange = useCallback(
    (type: 'adults' | 'children', delta: number) => {
      const currentValue = value as unknown as GuestFieldValue | undefined;
      const currentAdults = parseCount(currentValue?.adults, 1);
      const currentChildren = parseCount(currentValue?.children, 0);
      const updatedAdults = type === 'adults' ? currentAdults + delta : currentAdults;
      const updatedChildren = type === 'children' ? currentChildren + delta : currentChildren;
      const updated = {
        adults: String(updatedAdults),
        children: String(updatedChildren),
      } as unknown as TFieldValues[typeof name extends keyof TFieldValues ? typeof name : never];

      onChange(updated as any);
      onAfterChange?.(type, { adults: String(updatedAdults), children: String(updatedChildren) });
    },
    [value, onChange, onAfterChange]
  );

  const errorMessage = error ? String(error.message || (error as any).adults?.message || '') : '';

  return (
    <GuestPicker
      {...props}
      numberOfAdults={adults}
      numberOfChildren={children}
      onGuestCountChange={handleGuestCountChange}
      error={errorMessage}
    />
  );
};

export const RHFGuestPicker = memo(RHFGuestPickerInner) as typeof RHFGuestPickerInner;
